/**
 * Folder-mode plumbing for a library **asset** (CONTENT_MODEL_SPEC §2, §3.1).
 *
 * An asset is a folder exactly as a scene is: one glTF 2.1 document plus the
 * files it names, named by relative uris the reader resolves against the
 * document's own URL. So editing one costs **one JSON document + a row
 * update** (§2.2) — never a re-upload of a binary the folder already holds,
 * and never a ZIP rewrite.
 *
 * The one thing that is not like a scene is where names come from. Storage
 * names are minted by the **server** and by nothing else
 * (`assets/<slug>.<hash16>.<ext>`, §2.1), and `PATCH /assets/{id}` with a
 * document validates every relative `files[k].uri` against what the folder
 * actually holds, answering 400 on a mismatch — it never rewrites the
 * client's document, because the stored bytes must stay the client's bytes.
 * A client therefore learns a name one of two ways: from
 * `POST /assets/{id}/files` (this module's {@link assetFileUploader}) or from
 * the document it opened. Hence the two-step publish
 * ({@link publishAssetFolder}, through the same `saveFolder` a scene save
 * uses): send the new bytes first, repoint the document at the names that
 * came back — `files[]` and the behavior paths naming those files, never a
 * re-export — then PATCH the document alone.
 *
 * Everything here is a pure function over POJOs plus one module-level session
 * object, so it can be tested without a viewer or a network.
 */

import type { AssetFileResponseDto } from 'trace-viewer/cms';
import type { SceneFolder } from 'trace-viewer/editor';
import {
    TRACE_CONTENT_CONTAINER,
    saveFolder,
    type SceneFileUploader,
    type StoredFileRecords,
} from './scene-folder';
import { withContentStorage, type AssetContentStorageField } from './asset-writes';

// ── Addressing ──────────────────────────────────────────────────────

/**
 * Absolute URL of an asset's content folder, ending in `/`.
 *
 * A relative `assets/<slug>.<hash16>.<ext>` in the asset's document resolves
 * against this, and it is what an eager import registers so a just-uploaded
 * file still resolves after the in-memory blob is gone.
 *
 * Derived from the row's own url where it has one, so the folder is wherever
 * the bytes actually are (a path prefix, a CDN, a future rehoming) rather
 * than a container path we assume. But — unlike a scene's `rootContentUrl`,
 * which is always the document — an asset row's url may point at a **file**
 * inside the folder rather than at a document: an unedited leaf asset points
 * the row at `…/assets/{id}/assets/<slug>.<hash16>.glb` (§3.1). Taking that
 * url's directory would give `…/assets/{id}/assets/` and every relative uri
 * would resolve one level too deep. So the folder is cut at the asset id
 * segment, which is the folder's name whatever the row points at inside it.
 *
 * The fallback — `{blobBase}/trace-content/assets/{id}/` — covers a row with
 * no url and a legacy row whose bytes are still a raw blob in
 * `trace-cms-assets`: that asset has no folder *yet*, and this is where the
 * folder appears the moment its first document is written.
 */
export function assetFolderBaseUrl(
    blobBaseUrl: string,
    assetId: string,
    rowUrl?: string | null,
): string {
    const base = blobBaseUrl.replace(/\/$/, '');
    const fallback = `${base}/${TRACE_CONTENT_CONTAINER}/assets/${assetId}/`;
    if (!rowUrl || !assetId) return fallback;

    // A row url is container-relative (`trace-content/assets/{id}/…`) or
    // absolute; `resolveUrl` in catalog-api makes the same distinction.
    const absolute = /^https?:\/\//i.test(rowUrl)
        ? rowUrl
        : `${base}/${rowUrl.replace(/^\//, '')}`;

    try {
        const parsed = new URL(absolute);
        parsed.hash = '';
        parsed.search = '';
        const marker = `/${assetId}/`;
        const at = parsed.pathname.toLowerCase().indexOf(marker.toLowerCase());
        if (at < 0) return fallback;
        parsed.pathname = parsed.pathname.slice(0, at + marker.length);
        return parsed.toString();
    } catch {
        return fallback;
    }
}

// ── POST /assets/{id}/files ─────────────────────────────────────────

/**
 * What `POST /assets/{assetId}/files` needs from a client, and nothing more.
 *
 * Structurally satisfied by the generated `Assets` client (its `filesCreate`
 * takes an optional third `params` argument and answers an `AxiosResponse`),
 * and by a two-line fake in a test — which is the point of declaring it here
 * rather than reaching for `Pick<Assets, 'filesCreate'>`: an `AxiosResponse`
 * a test would have to forge in full is not part of what this module needs.
 */
export interface AssetFilesClient {
    filesCreate(
        assetId: string,
        data: { File?: File; TeamId?: string },
    ): Promise<{ data?: AssetFileResponseDto | null }>;
}

/**
 * The asset-folder uploader {@link publishAssetFolder} saves through.
 *
 * `SceneFileUploader` is named for where it was first needed, but its
 * contract is folder-neutral: *put these bytes in the folder owned by this
 * id, and tell me the relative uri it is stored under*. The `sceneId`
 * parameter is therefore **the folder owner's id** — an asset id here — and
 * the endpoint behind it is the asset twin of `POST /scenes/{id}/files`
 * (spec §2.4).
 *
 * The uri is the only thing worth taking from the response, and a response
 * without one is a failure however it was spelled: the client would go on to
 * PATCH a document naming a file the folder does not hold, and collect a 400
 * from the manifest validation with nothing useful in it. Throwing here says
 * which file, and says it before anything is written.
 */
export function assetFileUploader(
    client: AssetFilesClient,
    teamId: string | undefined,
): SceneFileUploader {
    return {
        async uploadSceneFile(assetId: string, file: Blob, fileName: string): Promise<{ uri: string }> {
            const upload = new File([file], fileName, { type: file.type || 'application/octet-stream' });
            const response = await client.filesCreate(assetId, { File: upload, TeamId: teamId });
            const uri = response?.data?.uri;
            if (!uri) {
                throw new Error(`Asset file import for "${fileName}" returned no uri`);
            }
            return { uri };
        },
    };
}

// ── The open asset's session ────────────────────────────────────────

/**
 * What the editor holds while a library asset is open for editing — the
 * asset-side analog of `CmsManager`'s per-scene state.
 *
 * A module-level singleton for the same reason `active-manager` is one: the
 * modules that need it (the import path, the publish path) are not on the
 * bootstrap path and cannot be handed one.
 */
export interface AssetFolderSession {
    /** The asset being edited — the folder owner. */
    readonly assetId: string;
    /** Team context the asset's write gate needs (`PATCH /assets/{id}`). */
    readonly teamId: string | undefined;
    /** Absolute URL the folder's relative uris resolve against, ending in `/`. */
    readonly baseUrl: string;
    /** `POST /assets/{assetId}/files`, already carrying the team id. */
    readonly uploader: SceneFileUploader;
}

/** What {@link openAssetFolderSession} needs to build a session. */
export interface AssetFolderSessionInit {
    assetId: string;
    teamId?: string;
    /** The asset row's own url, if known — see {@link assetFolderBaseUrl}. */
    rowUrl?: string | null;
    blobBaseUrl: string;
    client: AssetFilesClient;
}

let _session: AssetFolderSession | null = null;

/**
 * Begin the session for `assetId`.
 *
 * What the folder already holds is not kept here: the export into
 * `baseUrl` names it (files streamed from the folder, imports registered at
 * it, and what this session stored there, recorded on the scene's
 * resources by URL — so another asset's records never apply).
 */
export function openAssetFolderSession(init: AssetFolderSessionInit): AssetFolderSession {
    _session = {
        assetId: init.assetId,
        teamId: init.teamId,
        baseUrl: assetFolderBaseUrl(init.blobBaseUrl, init.assetId, init.rowUrl),
        uploader: assetFileUploader(init.client, init.teamId),
    };
    return _session;
}

/** The asset open for editing, or null when the editor is on a scene. */
export function getAssetFolderSession(): AssetFolderSession | null {
    return _session;
}

/** End the session. Called when the editor disconnects from its source asset. */
export function resetAssetFolderSession(): void {
    _session = null;
}

// ── Publishing the folder ───────────────────────────────────────────

/** The multipart a folder-asset PATCH sends: the document, and never bytes. */
export interface AssetDocumentPatch {
    TeamId?: string;
    File: File;
    Thumbnail?: File;
}

/** The I/O {@link publishAssetFolder} does, as the two calls it makes. */
export interface AssetFolderPublishDeps {
    /** `POST /assets/{assetId}/files` — one call per new file. */
    uploader: SceneFileUploader;
    /** `PATCH /assets/{assetId}` — one call, the document (two on a retry). */
    patch(assetId: string, parts: AssetDocumentPatch & AssetContentStorageField): Promise<unknown>;
    /**
     * The asset's folder URL and the scene's record of what this session
     * stored there. Absent for a brand-new asset: nothing is recorded, so
     * nothing is retried.
     */
    stored?: { folderUrl: string; resources: StoredFileRecords };
}

/** No records: a new asset's folder holds nothing a later save could skip. */
const NO_RECORDS: StoredFileRecords = {
    recordStoredFiles: () => Promise.resolve(),
    forgetStoredFiles: () => {},
    hasStoredFiles: () => false,
};

/** Identity and trimmings the PATCH carries beside the document. */
export interface AssetFolderPublishInput {
    assetId: string;
    teamId?: string;
    /** File name for the document part (`<title>.gltf`). */
    documentName: string;
    thumbnail?: File;
}

/** What a publish did, for the caller's log and its tests. */
export interface AssetFolderPublishSummary {
    /** Authored uris that were sent, in document order. */
    uploaded: string[];
    /** How many relative entries the folder already held. */
    existing: number;
    /** How many the exporter handed over bytes for. */
    total: number;
    /** Size of the document actually PATCHed, after repointing. */
    documentSize: number;
}

/**
 * Save an asset folder: new bytes first, then the document alone.
 *
 * The order is forced by who mints names (§2.1). The exporter authors a uri
 * from the file's own name (`assets/cylinder.glb`); the server stores it as
 * `assets/cylinder.<hash16>.glb` and will refuse — 400, with
 * `[{ index, authored, stored }]` — a document naming anything else. So the
 * files go up first, `uploadFolder` (through `saveFolder`) applies what came back to
 * `files[]` and the behavior paths naming them (the `externalAssets` and
 * `extras.trace.file` indices beside it are positional and must not move,
 * which is exactly why a re-export is the wrong fix), and only then does the
 * document travel.
 *
 * The PATCH carries **no `Files` part**. It used to carry both, in one
 * multipart, and that is the bug this replaces: the server stores the parts
 * under minted names and then compares the document against them, so a
 * single-shot write can only succeed when nothing new travels at all.
 *
 * `exportFolder` runs again if a refused PATCH is retried (`saveFolder`).
 */
export async function publishAssetFolder(
    deps: AssetFolderPublishDeps,
    exportFolder: () => Promise<SceneFolder>,
    input: AssetFolderPublishInput,
): Promise<AssetFolderPublishSummary> {
    const { folder, document } = await saveFolder({
        folderUrl: deps.stored?.folderUrl ?? '',
        resources: deps.stored?.resources ?? NO_RECORDS,
        exportFolder,
        ownerId: input.assetId,
        uploader: deps.uploader,
        // A `.gltf` is a DOCUMENT and a document can only be a folder, so
        // `withContentStorage` stamps `Folder` here in either build mode
        // (`asset-writes.ts`, CONTENT_MODEL_SPEC §6.1).
        write: (blob) => deps.patch(input.assetId, withContentStorage({
            TeamId: input.teamId,
            File: new File([blob], input.documentName, { type: 'model/gltf+json' }),
            Thumbnail: input.thumbnail,
        })),
    });

    console.log(
        `[AssetFolder] Updated asset ${input.assetId}: document (${document.size} bytes); `
        + `${folder.files.size} folder file(s) sent, ${folder.existing.size} already in the folder`,
    );

    return {
        uploaded: [...folder.files.keys()],
        existing: folder.existing.size,
        total: folder.files.size,
        documentSize: document.size,
    };
}
